/**
 * Sorting for the participants grid. Runs on the full filtered list *before* pagination,
 * otherwise a header click would only reorder the rows of the current page.
 */

import type { SortingState } from '@tanstack/react-table';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';

const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

// Logical order, not alphabetical: what needs attention comes first when ascending
const REGISTRATION_RANK: Record<string, number> = { PRE_REGISTERED: 0, VERIFIED: 1, REJECTED: 2 };
const PAYMENT_RANK: Record<string, number> = { PENDING: 0, PARTIAL: 1, PAID: 2, REVERSED: 3 };
const ATTENDANCE_RANK: Record<string, number> = { PENDING: 0, ATTENDED: 1, ABSENT: 2 };

const rank = (table: Record<string, number>, value: string | null | undefined) =>
  table[value ?? ''] ?? Number.MAX_SAFE_INTEGER;

export const participantName = (p: ParticipantWithDetails) =>
  (p.minister_id ? p.minister?.full_name : p.guest?.full_name) ?? '';

/** Same amount the "Precio" column shows */
export const participantDisplayPrice = (p: ParticipantWithDetails) =>
  Number((p.payment_status === 'PAID' ? p.payment_amount : p.calculated_price) ?? 0);

type Comparator = (a: ParticipantWithDetails, b: ParticipantWithDetails) => number;

const COMPARATORS: Record<string, Comparator> = {
  name: (a, b) => collator.compare(participantName(a), participantName(b)),
  location: (a, b) =>
    collator.compare(a.minister?.district?.name ?? '', b.minister?.district?.name ?? '') ||
    collator.compare(a.minister?.region?.name ?? '', b.minister?.region?.name ?? ''),
  price: (a, b) => participantDisplayPrice(a) - participantDisplayPrice(b),
  registration_status: (a, b) =>
    rank(REGISTRATION_RANK, a.registration_status) - rank(REGISTRATION_RANK, b.registration_status),
  payment_status: (a, b) => rank(PAYMENT_RANK, a.payment_status) - rank(PAYMENT_RANK, b.payment_status),
  attendance: (a, b) => rank(ATTENDANCE_RANK, a.attendance_status) - rank(ATTENDANCE_RANK, b.attendance_status),
  registration_date: (a, b) =>
    new Date(a.registration_date).getTime() - new Date(b.registration_date).getTime(),
};

/** Column ids that can be sorted */
export const SORTABLE_PARTICIPANT_COLUMNS = new Set(Object.keys(COMPARATORS));

/** Returns a new array; the input is left as the server sent it. No sorting → same order. */
export function sortParticipants(
  participants: ParticipantWithDetails[],
  sorting: SortingState,
): ParticipantWithDetails[] {
  const active = sorting[0];
  const compare = active && COMPARATORS[active.id];
  if (!compare) return participants;
  const dir = active.desc ? -1 : 1;
  // Array.sort is stable, so equal rows keep the server order
  return [...participants].sort((a, b) => dir * compare(a, b));
}

// ===================
// URL STATE (?sort=name:asc&size=50)
// ===================

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const DEFAULT_PAGE_SIZE = 25;

/** "name:desc" → sorting state. Unknown columns or directions are ignored (bad or old links). */
export function parseSortParam(raw: string | null): SortingState {
  const [id, dir] = (raw ?? '').split(':');
  if (!SORTABLE_PARTICIPANT_COLUMNS.has(id) || (dir !== 'asc' && dir !== 'desc')) return [];
  return [{ id, desc: dir === 'desc' }];
}

export function formatSortParam(sorting: SortingState): string | undefined {
  const active = sorting[0];
  return active ? `${active.id}:${active.desc ? 'desc' : 'asc'}` : undefined;
}

export function parsePageSizeParam(raw: string | null): number {
  const n = Number(raw);
  return PAGE_SIZE_OPTIONS.includes(n) ? n : DEFAULT_PAGE_SIZE;
}
